import { useEffect, useState } from "react";
import { Plugs, CheckCircle, XCircle, TrashSimple, ShieldCheck, LinkSimple, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function OdooConnections() {
  const [conn, setConn] = useState(null);
  const [form, setForm] = useState({ url: "", db: "", username: "", secret: "" });
  const [companies, setCompanies] = useState([]);
  const [odooCompanies, setOdooCompanies] = useState([]);
  const [busy, setBusy] = useState(null);

  const loadConn = () => api.get("/odoo/connection").then((res) => setConn(res.data));
  const loadCompanies = () => api.get("/companies").then((res) => setCompanies(res.data));
  useEffect(() => { loadConn(); loadCompanies(); }, []);

  const saveConn = async () => {
    const payload = {
      url: form.url || conn?.url || "",
      db: form.db || conn?.db || "",
      username: form.username || conn?.username || "",
      secret: form.secret || "",
      active: true,
    };
    if (!payload.url || !payload.db || !payload.username || !payload.secret) {
      toast.error("Completa URL, base de datos, usuario y API key.");
      return;
    }
    setBusy("save");
    try {
      await api.post("/odoo/connection", payload);
      toast.success("Conexión guardada (cifrada)");
      setForm({ url: "", db: "", username: "", secret: "" });
      loadConn();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const testConn = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/odoo/connection/test");
      toast.success(`Conexión OK · Odoo ${data.version || ""} (uid ${data.uid})`);
      loadConn();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const fetchOdooCompanies = async () => {
    setBusy("fetch");
    try {
      const { data } = await api.get("/odoo/companies");
      setOdooCompanies(data);
      toast.success(`${data.length} compañías Odoo encontradas`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const mapCompany = async (companyId, odooId) => {
    try {
      await api.post("/odoo/company-map", {
        company_id: companyId,
        odoo_company_id: odooId === "none" ? null : parseInt(odooId, 10),
      });
      toast.success("Mapeo actualizado");
      loadCompanies();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeConn = async () => {
    if (!window.confirm("¿Eliminar la conexión Odoo?")) return;
    await api.delete("/odoo/connection");
    toast.success("Conexión eliminada");
    setConn(null);
  };

  return (
    <div className="space-y-6">
      <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <ShieldCheck size={16} aria-hidden="true" />
        Una sola instancia (multicompañía) · credenciales cifradas (XML-RPC) · consultas a Odoo bajo demanda.
      </p>

      {/* Connection */}
      <div className="rounded-xl border border-border bg-card p-5" data-testid="odoo-connection-card">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
              <Plugs weight="duotone" size={22} aria-hidden="true" />
            </div>
            <div>
              <p className="font-heading text-lg font-semibold">Instancia Odoo</p>
              {conn ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {conn.status === "ok" ? (
                    <span className="flex items-center gap-1 text-success"><CheckCircle size={13} /> Conectado</span>
                  ) : conn.status === "error" ? (
                    <span className="flex items-center gap-1 text-danger"><XCircle size={13} /> Error</span>
                  ) : <span>Configurado</span>}
                  <span>· {conn.url}</span>
                </div>
              ) : <p className="text-xs text-muted-foreground">Sin configurar</p>}
            </div>
          </div>
          {conn && (
            <Button variant="ghost" size="icon" onClick={removeConn} data-testid="odoo-delete">
              <TrashSimple size={16} className="text-danger" />
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>URL de Odoo</Label>
            <Input placeholder="https://miempresa.odoo.com" defaultValue={conn?.url || ""}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} data-testid="odoo-url" />
          </div>
          <div className="space-y-1.5">
            <Label>Base de datos</Label>
            <Input placeholder="nombre_db" defaultValue={conn?.db || ""}
              onChange={(e) => setForm((f) => ({ ...f, db: e.target.value }))} data-testid="odoo-db" />
          </div>
          <div className="space-y-1.5">
            <Label>Usuario (bot)</Label>
            <Input placeholder="bot@empresa.com" defaultValue={conn?.username || ""}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} data-testid="odoo-user" />
          </div>
          <div className="space-y-1.5">
            <Label>API Key {conn && <span className="text-muted-foreground">(oculta — escribe para cambiar)</span>}</Label>
            <Input type="password" placeholder={conn ? "••••••••" : "API key de Odoo"}
              onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))} data-testid="odoo-secret" />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button onClick={saveConn} disabled={busy === "save"} data-testid="odoo-save">Guardar</Button>
          <Button variant="outline" onClick={testConn} disabled={!conn || busy === "test"} data-testid="odoo-test">
            <CheckCircle size={16} className="mr-1" aria-hidden="true" /> Probar conexión
          </Button>
          <Button variant="secondary" onClick={fetchOdooCompanies} disabled={!conn || busy === "fetch"} data-testid="odoo-fetch-companies">
            <DownloadSimple size={16} className="mr-1" aria-hidden="true" /> Cargar compañías de Odoo
          </Button>
        </div>
      </div>

      {/* Company mapping */}
      <div className="rounded-xl border border-border bg-card p-5" data-testid="odoo-mapping-card">
        <div className="mb-1 flex items-center gap-2">
          <LinkSimple size={18} weight="duotone" aria-hidden="true" />
          <h2 className="font-heading text-lg font-semibold">Empate de empresas</h2>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          Asocia cada planta de este sistema con su compañía en Odoo. Este empate sólo define qué compañía de Odoo se consulta cuando seleccionas la planta — la consulta se ejecuta bajo demanda al ver el dashboard o al usar "Probar" en una variable.
        </p>

        <div className="space-y-2">
          {companies.map((c) => {
            const mapped = odooCompanies.find((o) => o.id === c.odoo_company_id);
            return (
              <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`odoo-map-row-${c.code}`}>
                <div className="min-w-[180px] flex-1">
                  <p className="text-sm font-medium">{c.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">{c.code}</p>
                </div>

                <Select
                  value={c.odoo_company_id != null ? String(c.odoo_company_id) : "none"}
                  onValueChange={(v) => mapCompany(c.id, v)}
                >
                  <SelectTrigger className="h-9 w-[230px]" data-testid={`odoo-company-select-${c.code}`}>
                    <SelectValue placeholder="Compañía Odoo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin mapear —</SelectItem>
                    {odooCompanies.map((o) => (
                      <SelectItem key={o.id} value={String(o.id)}>{o.name} (#{o.id})</SelectItem>
                    ))}
                    {/* keep current mapping selectable even if list not loaded */}
                    {c.odoo_company_id != null && !mapped && (
                      <SelectItem value={String(c.odoo_company_id)}>Odoo #{c.odoo_company_id}</SelectItem>
                    )}
                  </SelectContent>
                </Select>

                {c.odoo_company_id != null ? (
                  <span className="ml-auto flex items-center gap-1 text-xs text-success" data-testid={`odoo-map-ok-${c.code}`}>
                    <CheckCircle size={14} aria-hidden="true" /> Empatada
                  </span>
                ) : (
                  <span className="ml-auto text-xs text-muted-foreground">Sin empatar</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-info/30 bg-info/10 p-4 text-sm text-info">
        Configura el mapeo de cada variable (modelo, método, campo, dominio) en el módulo de <strong>Variables</strong>
        {" "}(origen Automático/Odoo) y usa el botón <strong>Probar</strong> para validarlo. Las consultas se ejecutan en vivo, filtradas por la compañía Odoo de cada planta.
      </div>
    </div>
  );
}
